import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Mail, CheckCircle, Clock, Download, RefreshCw,
  ExternalLink, ChevronRight, Inbox, FileText, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { getEmailReceipts, parseEmailReceipt, importEmailReceipt, scanEmails } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

export default function EmailScanPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [emailContent, setEmailContent] = useState({ subject: '', from: '', body: '' });
  const [isParsing, setIsParsing] = useState(false);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const res = await getEmailReceipts();
      setReceipts(res.data.receipts || []);
    } catch (error) {
      console.error('Failed to load receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleParseEmail = async () => {
    if (!emailContent.body.trim()) {
      toast.error(t('paste_email_content'));
      return;
    }

    setIsParsing(true);
    try {
      const res = await parseEmailReceipt(emailContent);
      
      if (res.data.success) {
        toast.success(t('receipt_detected'));
        setShowPasteModal(false);
        setEmailContent({ subject: '', from: '', body: '' });
        loadReceipts();
      } else {
        toast.error(res.data.message || t('no_receipt_detected'));
      }
    } catch (error) {
      toast.error(t('analysis_error'));
    } finally {
      setIsParsing(false);
    }
  };

  const handleImportReceipt = async (receiptId) => {
    try {
      await importEmailReceipt(receiptId);
      toast.success(t('imported_as_transaction'));
      loadReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const pendingReceipts = receipts.filter(r => !r.imported);
  const importedReceipts = receipts.filter(r => r.imported);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {t('email_scan')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('auto_import_receipts')}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={loadReceipts}>
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Gmail Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="h-full cursor-pointer hover:border-red-300 transition-colors" onClick={() => setShowForwardModal(true)}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold">Gmail</p>
                    <p className="text-sm text-muted-foreground">
                      {t('forward_receipts')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Outlook Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="h-full cursor-pointer hover:border-blue-300 transition-colors" onClick={() => setShowForwardModal(true)}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8" viewBox="0 0 24 24">
                      <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.159.152-.353.228-.582.228h-8.616v-6.95l1.74 1.283c.086.063.186.094.3.094.112 0 .214-.031.304-.094l6.846-4.903a.61.61 0 0 0 .246-.712zm-.82-1.477L16.34 10.5l-1.776-1.31V4.65h8.616c.227 0 .421.075.58.225.16.15.24.342.24.578a.6.6 0 0 1-.18.457zM9.818 21.671H.582A.558.558 0 0 1 0 21.115V2.885c0-.16.058-.297.174-.408A.558.558 0 0 1 .582 2.3h9.236c.16 0 .297.058.408.174.11.117.166.253.166.408v18.23c0 .16-.055.297-.166.408a.546.546 0 0 1-.408.151zm-1.61-10.836c0-.927-.236-1.655-.71-2.183-.473-.528-1.124-.792-1.953-.792-.842 0-1.504.263-1.985.788-.482.526-.723 1.256-.723 2.191 0 .923.24 1.646.72 2.169.48.523 1.137.784 1.97.784.848 0 1.505-.259 1.972-.776.466-.518.7-1.245.7-2.181z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold">Outlook</p>
                    <p className="text-sm text-muted-foreground">
                      {t('forward_receipts')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Paste Email Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button 
            variant="outline" 
            className="w-full h-14"
            onClick={() => setShowPasteModal(true)}
          >
            <FileText className="w-5 h-5 mr-2" />
            {t('paste_confirmation_email')}
          </Button>
        </motion.div>

        {/* Pending Receipts */}
        {pendingReceipts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  {t('receipts_to_import')}
                  <span className="ml-auto text-sm font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    {pendingReceipts.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingReceipts.map((receipt) => (
                  <div 
                    key={receipt.receipt_id}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{receipt.merchant}</p>
                      <p className="text-sm text-muted-foreground truncate">{receipt.email_subject}</p>
                      <p className="text-lg font-bold text-primary mt-1">{formatCurrency(receipt.amount)}</p>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => handleImportReceipt(receipt.receipt_id)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {t('import')}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Imported Receipts */}
        {importedReceipts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  {t('imported_receipts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {importedReceipts.slice(0, 5).map((receipt) => (
                  <div 
                    key={receipt.receipt_id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="font-medium text-sm">{receipt.merchant}</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(receipt.amount)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Empty State */}
        {receipts.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="text-center py-12">
              <CardContent>
                <Inbox className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">
                  {t('no_email_receipts')}
                </h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  {t('paste_order_confirmation_instructions')}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-muted/30">
            <CardContent className="p-6">
              <h3 className="font-bold mb-4">
                {t('how_to_import')}
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">{t('copy_email')}</p>
                    <p className="text-muted-foreground">
                      {t('copy_paste_order_email')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">{t('paste_here')}</p>
                    <p className="text-muted-foreground">
                      {t('click_paste_email')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">{t('import')}</p>
                    <p className="text-muted-foreground">
                      {t('spenspot_auto_detect')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Forward Instructions Modal */}
      <Dialog open={showForwardModal} onOpenChange={setShowForwardModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('forward_receipts')}
            </DialogTitle>
            <DialogDescription>
              {t('auto_import_forward')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {t('forward_to')}
              </p>
              <p className="font-mono font-bold text-lg text-primary">receipts@spenspot.app</p>
            </div>
            
            <div className="flex items-start gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {t('feature_in_development')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForwardModal(false)}>
              {t('close')}
            </Button>
            <Button onClick={() => { setShowForwardModal(false); setShowPasteModal(true); }}>
              {t('paste_email')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paste Email Modal */}
      <Dialog open={showPasteModal} onOpenChange={setShowPasteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('paste_confirmation_email')}
            </DialogTitle>
            <DialogDescription>
              {t('paste_order_email_content')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">
                {t('subject_optional')}
              </label>
              <Input
                placeholder={t('subject_placeholder')}
                value={emailContent.subject}
                onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">
                {t('from_optional')}
              </label>
              <Input
                placeholder="Ex: auto-confirm@amazon.fr"
                value={emailContent.from}
                onChange={(e) => setEmailContent({ ...emailContent, from: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">
                {t('email_content_required')}
              </label>
              <Textarea
                placeholder={t('paste_email_text_html')}
                value={emailContent.body}
                onChange={(e) => setEmailContent({ ...emailContent, body: e.target.value })}
                rows={10}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasteModal(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleParseEmail} disabled={isParsing || !emailContent.body.trim()}>
              {isParsing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              {t('analyze')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
